import { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";

import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";

import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { getTokenOrderBySeller } from "src/api/tokenTransaction";

import font from "src/styles/font";
import device from "src/styles/device";
import {
  Black,
  DarkGrey,
  Grey,
  Primary,
  White,
  White01,
} from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency } from "src/common";
import { useDialog } from "src/context/DialogContext";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";
import TextField from "src/components/core/TextField";
import Chip from "src/components/core/Chip";

import OrderDetailDialog from "src/modules/seller-token-order/OrderDetailDialog";
import Button from "src/components/core/Button";

const Container = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px;
`;

const Wrapper = styled.div`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const SpanHeader = styled.div`
  color: ${Black};
  font-size: ${font.size.xl};
  font-weight: 600;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.mobileL} {
    font-size: ${font.size.lg};
  }
`;

const SpanSubHeader = styled.div`
  color: ${DarkGrey};
  font-size: ${font.size.md};
  font-weight: 400;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const SpanItemName = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.md};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 500)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const SpanSubItem = styled.div`
  color: ${({ color }) => (color ? color : Grey)};
  font-size: ${font.size.sm};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
  }

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
  }
`;

const WrapperFilter = styled(Wrapper)`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${device.tabletL} {
    flex-wrap: wrap;
  }
`;

const WrapperDataGrid = styled(Wrapper)`
  width: 100%;
  height: 600px;

  .MuiDataGrid-root {
    border: 1px solid ${White01};
    border-radius: 16px;
    overflow: hidden;
  }
  .MuiDataGrid-columnHeaders {
    background-color: rgba(249, 250, 251, 0.5);
  }
  .MuiDataGrid-root .MuiDataGrid-columnSeparator--sideRight {
    color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader {
    padding: 0 16px;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within {
    outline: none;
  }
  .MuiDataGrid-root .MuiDataGrid-cell {
    padding: 0 16px;
  }
  .MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
    border-bottom: solid #f5f5f5 1px 0 1px 0;
  }
  .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: #ffffff;
  }
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;
const ImageNotFound = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/not+found.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const SellerTokenOrder = ({ open }) => {
  const { openDialog, closeDialog } = useDialog();

  const [isLoading, setIsLoading] = useState(false);
  const defaultSearch = {
    keyword: "",
    page: 0,
    page_size: 10,
    status: "",
    start_date: null,
    end_date: null,
  };
  const [dataSearch, setDataSearch] = useState(defaultSearch);
  const [tokenBillingList, setTokenBillingList] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const fetchBillingList = async () => {
    try {
      setIsLoading(true);

      const { orders, pagination } = await getTokenOrderBySeller({
        ...dataSearch,
        page: dataSearch.page,
      });

      setTokenBillingList(orders);
      setRowCount(pagination.total);
    } catch (error) {
      console.error("Error fetching billing list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // fetch billing list when search keyword is empty (initial load or after clearing search)
    if (dataSearch.keyword === "") fetchBillingList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataSearch.keyword,
    dataSearch.page,
    dataSearch.status,
    dataSearch.start_date,
    dataSearch.end_date,
  ]);

  const columns = [
    {
      field: "transactionInfo",
      headerName: "เลขที่เอกสาร / วันที่",
      flex: 1.5,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params, index) => {
        return (
          <Box
            sx={{
              width: "100%",
              margin: "16px 0",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <SpanItemName mb={8}>
              {params.row?.order_no || "oreder no."}
            </SpanItemName>

            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: Grey }} />
              <SpanSubItem>
                {params.row.createdAt
                  ? dayjs(params.row.createdAt).format("DD/MM/YYYY HH:mm")
                  : "-"}
              </SpanSubItem>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "packageInfo",
      headerName: "รายการ",
      flex: 1.3,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params, index) => {
        return (
          <Box
            sx={{
              width: "100%",
              margin: "16px 0",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <SpanItemName mb={8}>
              {params?.row?.package?.name || "oreder no."}
            </SpanItemName>

            <Stack direction="row" alignItems="center" spacing={1}>
              <SpanSubItem>
                {params?.row?.payment_method === "credit_card"
                  ? `Credit Card`
                  : params?.row?.payment_method === "promptpay"
                    ? `PromptPay`
                    : params?.row?.payment_method === "truewallet"
                      ? `TrueWallet`
                      : `Other`}
              </SpanSubItem>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "price",
      headerName: "ยอดชำระ",
      flex: 0.7,
      headerAlign: "right",
      align: "right",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box sx={{ textAlign: "right" }}>
            <SpanItemName
              fontWeight={600}
            >{`${convertIntToCurrency(params.row.price, true)} ฿`}</SpanItemName>
            <SpanSubItem>inc. VAT</SpanSubItem>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "สถานะการชำระเงิน",
      flex: 1,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Chip
            icon={
              params.row?.status === "PAID" ? (
                <CheckCircleOutlinedIcon />
              ) : params.row?.status === "PENDING" ? (
                <AccessTimeIcon />
              ) : (
                <HighlightOffIcon />
              )
            }
            label={
              params.row?.status === "PAID"
                ? "ชำระแล้ว"
                : params.row?.status === "PENDING"
                  ? "รอชำระ"
                  : params.row?.status === "CANCELLED"
                    ? "ยกเลิก"
                    : params.row?.status === "EXPIRED"
                      ? "หมดอายุ"
                      : "ไม่สำเร็จ"
            }
            variant="secondary"
            color={
              params.row?.status === "PAID"
                ? "success"
                : params.row?.status === "PENDING"
                  ? "warning"
                  : "error"
            }
          />
        );
      },
    },
    // {
    //   field: "documentStatus", // ??
    //   headerName: "เอกสารหัก ณ ที่จ่าย",
    //   flex: 1,
    //   headerAlign: "center",
    //   align: "center",
    //   editable: false,
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params) => {
    //     return (
    //       <>
    //         <Button
    //           variant="outlined"
    //           size="small"
    //           startIcon={<DownloadIcon />}
    //         >
    //           {params.row?.documentStatus === "CHECKED"
    //             ? "ตรวจสอบแล้ว"
    //             : params.row?.documentStatus === "PENDING"
    //               ? "แนบเอกสาร"
    //               : params.row?.documentStatus === "UPLOADED"
    //                 ? "ส่งเอกสารแล้ว"
    //                 : "-"}
    //         </Button>
    //       </>
    //     );
    //   },
    // },
    {
      field: "actions",
      type: "actions",
      headerName: "รายละเอียด",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Stack direction="row" alignItems="center" justifyContent="center">
            <IconButton
              aria-label="view"
              onClick={() =>
                openDialog({
                  type: "custom",
                  customDialog: (
                    <OrderDetailDialog
                      onClose={() => closeDialog()}
                      orderDetail={params.row}
                    />
                  ),
                })
              }
            >
              <VisibilityIcon sx={{ color: Grey }} />
            </IconButton>

            {checkNotEmpty(params.row?.receipt_file_url) && (
              <IconButton
                aria-label="download"
                onClick={() =>
                  window.open(params.row?.receipt_file_url, "_blank")
                }
              >
                <DownloadIcon sx={{ color: Primary }} />
              </IconButton>
            )}
          </Stack>
        );
      },
    },
  ];

  const handlePageChange = (newPage) => {
    setDataSearch({ ...dataSearch, page: newPage });
  };

  const onSearchButton = () => {
    setDataSearch({ ...dataSearch, page: 0 });
    fetchBillingList();
  };

  function NoRowsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNoData></ImageNoData>
        <TextNoData>No Data</TextNoData>
      </Stack>
    );
  }

  function NoResultsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNotFound></ImageNotFound>
        <TextNoData>Not Found</TextNoData>
      </Stack>
    );
  }

  return (
    <>
      <Main open={open}>
        <Container>
          <Grid container columnSpacing={2} rowSpacing={3}>
            <Grid item xs={12}>
              <SpanHeader mb={2}>ประวัติการสั่งซื้อและใบเสร็จ</SpanHeader>
              <SpanSubHeader>
                จัดการเอกสารทางบัญชี ดาวน์โหลดใบเสร็จ และนำส่งหัก ณ ที่จ่าย
              </SpanSubHeader>
            </Grid>

            <Grid item xs={12}>
              <WrapperFilter>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexWrap={{ xs: "wrap", md: "unset" }}
                >
                  <TextField
                    placeholder="ค้นหาเลขอ้างอิง..."
                    sx={{ width: { xs: "100%", sm: 320 } }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          id="button-search-quotation"
                          sx={{ padding: 0 }}
                          onClick={() => onSearchButton()}
                        >
                          <SearchIcon
                            sx={{
                              color: "#BF0013",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      ),
                    }}
                    value={dataSearch.keyword}
                    onChange={(e) =>
                      setDataSearch({
                        ...dataSearch,
                        keyword: e.target.value,
                      })
                    }
                    onKeyUp={(e) =>
                      e.code === "Enter" ? onSearchButton() : null
                    }
                  />

                  <FormControl
                    sx={{
                      minWidth: 160,
                      "& .MuiInputBase-input": {
                        padding: "12px 16px",
                      },
                      "& .MuiInputLabel-root": {
                        transform: "translate(14px, 12px) scale(1)",
                      },
                      "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiInputLabel-shrink":
                        {
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                    }}
                  >
                    <InputLabel id="status-select-label">
                      สถานะการเติม
                    </InputLabel>
                    <Select
                      labelId="status-select-label"
                      id="status-select"
                      label="สถานะการเติม"
                      value={dataSearch.status}
                      onChange={(e) =>
                        setDataSearch({
                          ...dataSearch,
                          status: e.target.value,
                          page: 0,
                        })
                      }
                    >
                      <MenuItem value={""}>ทุกสถานะ</MenuItem>
                      <MenuItem value={"PENDING"}>รอชำระ</MenuItem>
                      <MenuItem value={"PAID"}>ชำระแล้ว</MenuItem>
                      <MenuItem value={"CANCELLED"}>ยกเลิก</MenuItem>
                      <MenuItem value={"EXPIRED"}>หมดอายุ</MenuItem>
                      <MenuItem value={"FAILED"}>ไม่สำเร็จ</MenuItem>
                    </Select>
                  </FormControl>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="start date"
                      placeholder="ระบุวันที่"
                      inputFormat="DD/MM/YYYY"
                      value={dataSearch?.start_date}
                      onChange={(newValue) =>
                        setDataSearch({
                          ...dataSearch,
                          start_date: newValue.$d,
                          page: 0,
                        })
                      }
                      maxDate={
                        dataSearch?.end_date ? dataSearch?.end_date : null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{ ...params.inputProps, readOnly: true }}
                          variant="outlined"
                          sx={{
                            maxWidth: 180,
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="end date"
                      placeholder="ระบุวันที่"
                      inputFormat="DD/MM/YYYY"
                      value={dataSearch?.end_date}
                      onChange={(newValue) =>
                        setDataSearch({
                          ...dataSearch,
                          end_date: newValue.$d,
                          page: 0,
                        })
                      }
                      minDate={
                        dataSearch?.start_date ? dataSearch?.start_date : null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{ ...params.inputProps, readOnly: true }}
                          variant="outlined"
                          sx={{
                            maxWidth: 180,
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <Button
                    variant="text"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={() => setDataSearch(defaultSearch)}
                  >
                    Clear Filter
                  </Button>
                </Box>

                {/* <Box
                  sx={{
                    width: "max-content",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Button variant="contained" startIcon={<DownloadIcon />}>
                    Bulk Download (zip)
                  </Button>
                </Box> */}
              </WrapperFilter>
            </Grid>

            {/* package detail table */}
            <Grid item xs={12}>
              <WrapperDataGrid>
                <DataGrid
                  rows={tokenBillingList}
                  getRowId={(row) => row._id}
                  getRowHeight={() => "auto"}
                  loading={isLoading}
                  columns={columns}
                  pageSize={10}
                  rowCount={rowCount}
                  paginationMode="server"
                  page={dataSearch.page}
                  onPageChange={(e) => handlePageChange(e)}
                  pagination
                  disableSelectionOnClick
                  components={{
                    NoRowsOverlay,
                    NoResultsOverlay,
                  }}
                  componentsProps={{
                    basePopper: {},
                  }}
                />
              </WrapperDataGrid>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default SellerTokenOrder;
